import type { WorkflowWebhookProviderKey } from '@cotera/client/app/etc/api/typed-client';
import type {
  WebhookProviderConfigDraft,
  WebhookTriggerSettings,
} from './types';

// Provider-key parsing is owned by the shared API contract enum
// (`@cotera/api`); re-exported here so client webhook code has one parser and
// no hand-maintained key lists that drift out of sync.
export { parseWebhookProviderKey } from '@cotera/api';

const DEFAULT_VERIFICATION_CONFIG = { profile: 'cotera' as const };

export function createDefaultWebhookProviderConfigDraft(
  providerKey: WorkflowWebhookProviderKey = 'generic'
): WebhookProviderConfigDraft {
  return {
    name: 'Webhook',
    providerKey,
    eventKey: null,
    eventConfig: {},
    verificationConfig: DEFAULT_VERIFICATION_CONFIG,
    endpointSecret: '',
    enabled: true,
  };
}

/**
 * A provider with exactly one event offers no choice, so making the user click
 * it is pure friction — and every create flow gates its save on `eventKey`
 * being set, which makes the unclicked card look like the form is broken.
 * Returns the key to select, or undefined when there is nothing to decide
 * (several events, none at all, or one already chosen).
 */
export function soleWebhookEventKeyToSelect(params: {
  events: readonly { key: string }[];
  selectedEventKey: string | null;
}): string | undefined {
  if (params.selectedEventKey !== null || params.events.length !== 1) {
    return undefined;
  }
  return params.events[0]?.key;
}

export function webhookTriggerSettingsToConfigDraft(
  settings: WebhookTriggerSettings
): WebhookProviderConfigDraft {
  return {
    name: settings.name,
    providerKey: settings.providerKey,
    eventKey: settings.eventKey,
    eventConfig: toWebhookConfigRecord(settings.eventConfig),
    verificationConfig: settings.verificationConfig,
    endpointSecret: '',
    enabled: settings.enabled,
  };
}

/**
 * Inbound email address off a webhook trigger's event config, or undefined
 * before the trigger has been saved. Storage keeps only the local part, so this
 * is only ever a whole address when it came from a read path that applied the
 * domain — trigger settings, not the workflow graph.
 */
export function readInboundEmailAddress(
  eventConfig: unknown
): string | undefined {
  const address = toWebhookConfigRecord(eventConfig)['address'];
  return typeof address === 'string' && address !== '' ? address : undefined;
}

export function toWebhookConfigRecord(value: unknown): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
}
